//@flow
import axios from 'axios';
import Config from 'Config';
import { errorLogger, infoLogger } from '../helpers';

type HTTPMethod = 'POST' | 'GET' | 'PUT' | 'DELETE' | 'PATCH' | 'CONNECT' | 'OPTIONS' | 'TRACE' | 'HEAD';

const headers = (isTransfer = false) => {
  return {
    'x-correlation-id': window.__CORRELATION_ID__,
    'x-session-id': window.__SESSION_ID__,
    'x-customer-type': window.__CUSTOMER_TYPE__,
    'x-wall-password': window.__WALL_PASSWORD__,
    'x-page': window.__PAGE__,
    'x-vendor': window.__VENDOR__,
    'x-imsi': window.__IMSI__,
    'x-msisdn': window.__MSISDN__,
    'x-is-transfer': isTransfer ? 'true' : 'false',
  }
};

export default {
  rest: (url: string, data: {} = {}, method: HTTPMethod = 'GET', token: string) => {
    token = token === undefined ? '' : `Bearer ${token}`;

    return axios({
      url: `${Config.API_HOST}/api/${url}`,
      method,
      data,
      headers: {
        ...headers(),
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: token,
      },
    }).then(async (response) => {
      if (response.status >= 400) {
        throw new Error();
      }
      return response.data;
    });
  },
  getToggles: (data: {}) => {
    try {
      infoLogger({
        name: 'log-post-data:',
        log: `POST DATA: ${window.__POST_DATA__}`,
      });
      return axios({
        url: `/api/v1/getToggles/`,
        data,
        method: 'POST',
        headers: headers(),
      })
        .then(async (response) => {
          if (response.status >= 400) {
            return new Error();
          }
          return response;
        })
        .catch((error) => {
          return new Error(error);
        });
    } catch (error) {
      throw new Error(error);
    }
  },
  getMsisdn: (data: {}) => {
    return axios({
      url: `/api/v1/msisdn/`,
      data: data,
      method: 'POST',
      headers: headers(),
    })
      .then(async (response) => {
        if (response.status >= 400) {
          throw new Error();
        }
        return response.data;
      })
      .catch((error) => {
        return new Error(error);
      });
  },
  maintenanceLogin: (data: {}) => {
    return axios({
      url: `/api/v1/maintenanceLogin/`,
      data: data,
      method: 'POST',
      headers: headers(),
    })
      .then(async (response) => {
        if (response.status >= 400) {
          throw new Error();
        }

        window.__WALL_PASSWORD__ = data.password;
        return response.data;
      })
      .catch((error) => {});
  },
  login: (data: {}) => {
    return axios({
      url: `/api/v1/login/`,
      data: data.loginData,
      method: 'POST',
      headers: headers(data.isTransfer),
    })
      .then(async (response) => {
        return response.data;
      })
      .catch((error) => {
        errorLogger({ name: 'client-login-api', log: `Login failed` });
        throw error;
      });
  },
  getSubscriptions: (data: {}) => {
    return axios({
      url: `/api/v2/subscriptions/`,
      data,
      method: 'POST',
      headers: headers(data.isTransfer),
    })
      .then(async (response) => {
        if (response.status >= 400) {
          throw new Error();
        }
        return response.data;
      })
      .catch((error) => {
        return new Error(error);
      });
  },
  sendSmsCode: (data: {}) => {
    try {
      return axios({
        url: `/api/v1/sendSmsChallenge/`,
        data: data,
        method: 'POST',
        headers: headers(),
      }).then(async (response) => {
        return response.data;
      });
    } catch (error) {
      throw new Error();
    }
  },
  sendValidateSmsChallange: (data: {}) => {
    try {
      return axios({
        url: `/api/v1/validateSmsChallenge/`,
        data: data.loginData,
        method: 'POST',
        headers: headers(data.isTransfer),
      }).then(
        async (response) => {
          if (response.status >= 400) {
            throw Error();
          }
          return response;
        },
        (error) => {
          throw new Error();
        }
      );
    } catch (error) {
      throw new Error();
    }
  },
  getCustomerTypes: (data: {}) => {
    try {
      return axios({
        url: `/api/v1/customerTypes/`,
        data,
        method: 'POST',
        headers: headers(),
      })
        .then(async (response) => {
          if (response.status >= 400) {
            return new Error();
          }
          return response;
        })
        .catch((error) => {
          return new Error(error);
        });
    } catch (error) {
      throw new Error(error);
    }
  },
  reserveSimProfile: (data: {}) => {
    return axios({
      url: `/api/v1/transfers/iccid`,
      data,
      method: 'POST',
      headers: headers(true),
    })
      .catch((error) => {
        errorLogger({ name: 'client-transfer-api', log: `Reserve SIM profile failed` });
        throw new Error(`Reservering av simkort misslyckades: ${error.message}`);
      })
      .then(async (response) => {
        if (response.status >= 400) {
          errorLogger({
            name: 'client-transfer-api',
            log: `reserveSimProfile returned with status: ${response.status}`,
          });
          throw new Error(`Reservering av ny SIM-profil misslyckades, med statuskod: ${response.status}`);
        }
        return response;
      });
  },
  postTransferOrder: (data: {}) => {
    return axios({
      url: `/api/v1/transfers/`,
      data,
      method: 'POST',
      headers: headers(true),
    })
      .catch((error) => {
        errorLogger({ name: 'client-transfer-api', log: `postTransferOrder failed` });
        throw new Error(`Transfers misslyckades: ${error.message}`);
      })
      .then(async (response) => {
        if (response.status >= 400) {
          errorLogger({
            name: 'client-transfer-api',
            log: `postTransferOrder returned with status: ${response.status}`,
          });
          throw new Error(`Transfer-ordern misslyckades, med statuskod: ${response.status}`);
        }
        return response.data;
      });
  },
  postCustomerStates: (data: {}) => {
    const status = 400;
    return axios({
      url: `/api/v1/customerStates/`,
      data,
      method: 'POST',
      headers: headers(),
    })
      .catch((error) => {
        errorLogger({ name: 'client-customerStates-request', log: `Updating customer states failed` });
        throw new Error(`Uppdatering av customer states misslyckades: ${error.message}`);
      })
      .then(async (response) => {
        if (response.status >= 400) {
          errorLogger({
            name: 'client-customerStates-request',
            log: `customerStates returned with status: ${response.status}`,
          });
          throw new Error(`POST till customerStates misslyckades med status: ${status}`);
        }
        return response.data;
      });
  },
  postErrorLog: (data: {}) => {
    try {
      return axios({
        url: `/api/v1/errorLogs/`,
        data,
        method: 'POST',
        headers: headers(),
      })
        .then(async (response) => {
          if (response.status >= 400) {
            console.log('Error in try in api index.js');
            return new Error();
          }
          return response;
        })
        .catch((error) => {
          console.log('Error in catch in postErrorLog api index.js');
          return new Error(error);
        });
    } catch (error) {
      console.log('Error in catch 2 in postErrorLog api index.js');
      throw new Error(error);
    }
  },
  postInfoLog: (data: {}) => {
    try {
      return axios({
        url: `/api/v1/infoLogs/`,
        data,
        method: 'POST',
        headers: headers(),
      })
        .then(async (response) => {
          if (response.status >= 400) {
            console.log('Error in try in api index.js');
            return new Error();
          }
          return response;
        })
        .catch((error) => {
          console.log('Error in catch in postErrorLog api index.js');
          return new Error(error);
        });
    } catch (error) {
      console.log('Error in catch 2 in postErrorLog api index.js');
      throw new Error(error);
    }
  },
};
